import { Empty } from '@byecode/ui'
import { getAssetUrl } from '@lighthouse/assets'
import { DIRECTION } from '@lighthouse/core'
import { mergeRefs } from '@lighthouse/tools'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'

import { getPaddingBottomStyle, getPaddingStyle } from '../../utils/design'
import { getLayoutStyle, getSizeStyle } from '../../utils/layout'
import type { ContainerLayoutContextValue, FlowLayoutContextValue } from './Context'
import { ContainerLayoutProvider, DEFAULT_LAYOUT_CONTEXT_VALUE, FlowLayoutProvider } from './Context'
import { HighlightOverlay } from './HighlightOverlay'
import { NodeRender } from './NodeRender'

const Container = styled.div<{ disabled?: boolean }>`
    position: relative;
    z-index: 1;
    margin: 0 auto; ;
`

interface LayoutEngineProps extends Omit<FlowLayoutContextValue, 'rootElement'> {
    style?: React.CSSProperties
    sortable?: boolean
}

export const LayoutEngine = React.forwardRef<HTMLDivElement, LayoutEngineProps>((props, ref) => {
    const {
        data,
        size,
        layout,
        design,
        style,
        sortable,
        disabled,
        selectedNodes,
        nodeRender,
        highlight,
        scale,
        useParseVariableWithSubscription,
        useVisibilityFilter
    } = props
    const [root, setRoot] = useState<HTMLDivElement | null>(null)

    // ✅ 避免使用 ...rest 展开，显式列出所有依赖项
    const flowLayoutContextValue = useMemo(
        () => ({
            ...DEFAULT_LAYOUT_CONTEXT_VALUE,
            data,
            rootElement: root,
            size,
            layout,
            design,
            sortable,
            disabled,
            selectedNodes,
            nodeRender,
            highlight,
            scale,
            useParseVariableWithSubscription,
            useVisibilityFilter
        }),
        [
            data,
            design,
            disabled,
            highlight,
            layout,
            nodeRender,
            root,
            scale,
            selectedNodes,
            size,
            sortable,
            useParseVariableWithSubscription,
            useVisibilityFilter
        ]
    )

    const containerLayoutContextValue = useMemo<ContainerLayoutContextValue>(
        () => ({ size, layout, design, level: 1 }),
        [design, layout, size]
    )

    const paddingBottom = getPaddingBottomStyle(layout?.padding, sortable)
    // const paddingTop = getPaddingTopStyle(layout?.padding, sortable)

    return (
        <FlowLayoutProvider value={flowLayoutContextValue}>
            <Container
                ref={mergeRefs([setRoot, ref])}
                data-layout-node="root"
                tabIndex={0}
                disabled={disabled}
                style={{
                    ...style,
                    ...getSizeStyle(size, DIRECTION.vertical, sortable),
                    ...getLayoutStyle(layout, sortable),
                    ...getPaddingStyle(layout?.padding, sortable)
                }}
            >
                {!disabled && data.length === 0 ? (
                    <Empty
                        style={{ pointerEvents: 'none' }}
                        icon={<img width={200} src={getAssetUrl('empty', 'no_component.svg')} alt="未找到数据" />}
                        description="将「组件」拖拽到此区域"
                    />
                ) : (
                    <ContainerLayoutProvider value={containerLayoutContextValue}>
                        {data.map(item => (
                            <NodeRender key={item.id} data={item} />
                        ))}
                    </ContainerLayoutProvider>
                )}

                <HighlightOverlay />
                {/* {isBottomAdOverScroll &&
                    (isAndroidOS()
                        ? <TopAdOverScroll top={paddingTop} />
                        : <BottomAdOverScroll bottom={paddingBottom} />)} */}
                {/* <BottomAdOverScroll bottom={paddingBottom} /> */}
                {/* <TopAdOverScroll /> */}
                {/* {rest.disabled ? null : <OutlineWrapper />} */}
            </Container>
        </FlowLayoutProvider>
    )
})
